import { useDispatch, useStore } from 'react-redux'

import { fetchingAPI } from '@/api/fetchingAPI'
import { apiService } from '@/api/apiService'
import { apiErrorMessage } from '@/api/handleResponse'
import { toast } from '@/components/toasts/toast.helpers'
import {
  prependSession,
  removeSession,
  renameSession,
  setSessionFavorite,
  setSessions,
} from '@/reducers/kodi/kodi.redux'

// handleResponse rejects a 409 with the raw `{detail}` body and a 404 with an
// Error tagged `status`; anything else (403, 5xx) is not about the chosen turn.
const isUnusableTurn = (error) => error?.status === 404 || !(error instanceof Error)

export const useKodiSessionMutations = () => {
  const dispatch = useDispatch()
  const store = useStore()

  const rename = (session, newName) => {
    const previousName = session.name
    dispatch(renameSession({ session_id: session.session_id, name: newName }))
    return fetchingAPI(
      `${apiService.kodi}/sessions/${session.session_id}`,
      'PATCH',
      dispatch,
      JSON.stringify({ name: newName })
    )
      .then((updated) => {
        if (updated && updated.name && updated.name !== newName) {
          dispatch(renameSession({ session_id: session.session_id, name: updated.name }))
        }
        toast.success('Session renamed')
        return updated
      })
      .catch((error) => {
        dispatch(renameSession({ session_id: session.session_id, name: previousName }))
        toast.error('Failed to rename session', { cause: error })
        throw error
      })
  }

  const remove = (session) => {
    const previousSessions = store.getState().kodi.sessions
    dispatch(removeSession({ session_id: session.session_id }))
    return fetchingAPI(`${apiService.kodi}/sessions/${session.session_id}`, 'DELETE', dispatch)
      .then((result) => {
        toast.success('Session deleted')
        return result
      })
      .catch((error) => {
        dispatch(setSessions(previousSessions))
        toast.error('Failed to delete session', { cause: error })
        throw error
      })
  }

  const toggleFavorite = async (session) => {
    const isFavorite = !session.is_favorite
    dispatch(setSessionFavorite({ session_id: session.session_id, is_favorite: isFavorite }))
    try {
      const updated = await fetchingAPI(
        `${apiService.kodi}/sessions/${session.session_id}`,
        'PATCH',
        dispatch,
        JSON.stringify({ is_favorite: isFavorite })
      )
      toast.success(isFavorite ? 'Session saved as favorite' : 'Session removed from favorites')
      return updated
    } catch (error) {
      dispatch(setSessionFavorite({ session_id: session.session_id, is_favorite: !isFavorite }))
      toast.error(
        isFavorite
          ? 'Failed to save session as favorite'
          : 'Failed to remove session from favorites',
        { cause: error }
      )
      throw error
    }
  }

  // Copies the session through the given answer turn (or, with none, the
  // server's latest) into a new session owned by the caller. Resolves with the
  // new session row, or null on failure.
  const fork = async (sessionId, auditUuid) => {
    // isSilent: handleResponse toasts 5xx itself; we toast once below instead.
    const post = (body) =>
      fetchingAPI(
        `${apiService.kodi}/sessions/${sessionId}/fork`,
        'POST',
        dispatch,
        JSON.stringify(body),
        undefined,
        { isSilent: true }
      )
    try {
      let created
      try {
        created = await post({ audit_uuid: auditUuid })
      } catch (error) {
        // The turn on screen may carry a uuid the server never stamped an answer
        // for (stopped mid-tool: 409) or doesn't know (404). The server's own
        // latest answer is the right cut then, so ask for that instead.
        if (auditUuid === undefined || !isUnusableTurn(error)) {
          throw error
        }
        created = await post({})
      }
      dispatch(prependSession(created))
      toast.success('Session duplicated')
      return created
    } catch (error) {
      // 403 arrives as an Error carrying the backend's message, 404 as an Error
      // with the body on `data`, and 409 as the raw `{detail}` body.
      const detail = error?.detail ?? error?.data?.detail
      toast.error(
        typeof detail === 'string' ? detail : apiErrorMessage(error, 'Failed to duplicate session')
      )
      return null
    }
  }

  return { rename, remove, toggleFavorite, fork }
}
